import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, TrendingUp } from "lucide-react";
import { api } from "../../api";
import { MEMBER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { GymLoader } from "../../components/common/GymLoader";

const TYPE_LABEL = { weight: "Weight", measurement: "Measurement", pr: "Personal record", assessment: "Assessment", streak: "Streak" };

// UX-MEM-01 §1 SCR-P03-05: read-only (progress entries are coach/staff-recorded, see
// routers/member_app.py) — `is_milestone` is a real, deterministic flag computed server-side
// from the exact same rule `crm_engine._detect_milestone` uses (PR entries always; streak
// entries every 5th), not a fabricated "always celebrate" gimmick.
export default function Progress() {
  const reducedMotion = useReducedMotion();
  const entriesQ = useQuery({ queryKey: ["member-progress"], queryFn: async () => (await api.get("/member/progress")).data.entries });

  return (
    <div data-testid={MEMBER.progressPage} className="space-y-1">
      <motion.h1 {...fadeUp(reducedMotion)} className="font-heading text-2xl font-bold mb-4 flex items-center gap-2"><TrendingUp size={20} color="#D6202B" /> My progress</motion.h1>

      {entriesQ.isLoading && <GymLoader label="Loading progress…" testId="member-progress-loader" />}
      {entriesQ.data?.length === 0 && <p className="text-sm" style={{ color: "#A1A1AA" }}>No progress entries logged yet — your coach will add these after sessions.</p>}

      <div className="pl-5 space-y-5" style={{ borderLeft: "1px solid rgba(255,255,255,0.1)" }}>
        {(entriesQ.data || []).slice().reverse().map((e, i) => (
          <motion.div
            key={e.id} {...fadeUp(reducedMotion, 0.03 * i)} data-testid={MEMBER.progressEntry(e.id)}
            className="relative rounded-2xl p-4 -ml-[26px] pl-[30px]"
            style={{ background: "#141414", border: e.is_milestone ? "1px solid #32D74B" : "1px solid rgba(255,255,255,0.1)", boxShadow: e.is_milestone ? "0 0 16px rgba(50,215,75,0.25)" : "none" }}
          >
            <div className="absolute left-[-5px] top-5 w-2.5 h-2.5 rounded-full" style={{ background: e.is_milestone ? "#32D74B" : "#3f3f46" }} />
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "#A1A1AA" }}>{TYPE_LABEL[e.type] || e.type}</span>
              {e.is_milestone && (
                <motion.span
                  data-testid={MEMBER.progressMilestoneBadge(e.id)} initial={{ scale: 0.8 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300 }}
                  className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(50,215,75,0.15)", color: "#32D74B" }}
                >
                  <Sparkles size={11} /> New milestone
                </motion.span>
              )}
            </div>
            <p className="font-heading text-2xl font-bold mt-1">{e.value}{e.unit ? ` ${e.unit}` : ""}</p>
            <p className="text-xs mt-1" style={{ color: "#A1A1AA" }}>{new Date(e.at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
